import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { useAccount, useLogin } from '@bnnvara/accounts';
import { TrackEvent } from '@bnnvara/analytics';
import { useBrand } from '@bnnvara/brand';
import { sizes } from '@bnnvara/breakpoints';
import { FollowBrandButton as StyledFollowBrandButton } from '@bnnvara/buttons';
import { useLocalStorage } from '@bnnvara/react-hooks';
import debounce from 'lodash/debounce';
import React, { Fragment, useEffect, useState } from 'react';

import { followRequest } from '../../api/mutations/brandFollow.gql';
import { FetchIsBrandFollowed } from '../../api/queries/me/brand.gql';
import { useNotifications } from '../../hooks/notifications';
import Notification from '../Notification';
import { copy } from './copyNotification';
import * as S from './styled';

const FollowBrand = () => {
  const brand = useBrand();
  const brandId = brand.id;
  const {
    setNotificationsHistory,
    getUniqueNotifications,
  } = useNotifications();
  const { login, isLoggedIn } = useLogin();
  const {
    token,
    profile: { UID },
  } = useAccount();
  const apolloClient = useApolloClient();

  const [isFollowedByUser, setIsFollowedByUser] = useState(false);
  const [
    userSubscribedToFirstBrand,
    setUserSubscribedToFirstBrand,
  ] = useLocalStorage('isFollowingBrand', false);

  const [isDesktop, setIsDesktop] = useState(false);

  const handleClick = async (followMutation) => {
    if (!isLoggedIn) return login();

    // TODO: add FollowedBrandsQuery to check if user follows any title(s) instead of current title (will do that icw with onBoarding going live)
    setIsFollowedByUser(!isFollowedByUser);

    if (!isFollowedByUser) {
      TrackEvent('header', 'click', 'volgen');
    } else {
      TrackEvent('header', 'click', 'ontvolgen');
    }

    return followMutation({
      variables: {
        interactionId: brand.id,
        token,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        followRequest: true,
      },
    });
  };

  const [followContent] = useMutation(followRequest, {
    onCompleted: () => {
      // If query is completed notify the user with a notification that he/she is subscribed to a brand.
      // if this is the first brand that the user is following, show a different message.
      if (isFollowedByUser) {
        const latestMessage = {
          action: userSubscribedToFirstBrand
            ? 'subscribedToBrand'
            : 'subscribedToFirstBrand',
          slug: userSubscribedToFirstBrand
            ? `/${brand.slug}`
            : '/wijzijnvoor-jijook',
          isRead: false,
          brand: {
            slug: brand.slug === 'portal' ? '' : brand.slug,
            name: brand.name,
          },
        };

        setNotificationsHistory((prevState) =>
          getUniqueNotifications(latestMessage, prevState)
        );
      }
      // Set a bool in localstorage so we know when to show which notification
      setUserSubscribedToFirstBrand(true);
    },
    onError: () => setIsFollowedByUser(!isFollowedByUser),
  });

  useEffect(() => {
    const fetchAndSetFollow = async () => {
      const { data } = await apolloClient.query({
        query: FetchIsBrandFollowed,
        variables: {
          interactionId: brandId,
          userId: UID,
        },
        fetchPolicy: 'network-only',
        options: {
          pollInterval: 3600000,
        },
      });
      setIsFollowedByUser(data.me.isBrandFollowed);
    };

    // default value, but necessary to update state if user logs out during session.
    if (!isLoggedIn) {
      setIsFollowedByUser(false);
    }

    if (isLoggedIn) {
      fetchAndSetFollow();
    }
  }, [apolloClient, isLoggedIn, brandId, UID]);

  // TODO: this handleResize fn might be prettier as a global hook
  const handleResize = () =>
    setIsDesktop(!process.browser || (window && window.innerWidth >= sizes.lg));
  const handleResizeDebounced = debounce(handleResize, 300);

  useEffect(() => {
    if (window) {
      window.addEventListener('resize', handleResizeDebounced);
      handleResize();
    }
    return () => window.removeEventListener('resize', handleResizeDebounced);
  }, [handleResizeDebounced]);

  return (
    <Fragment>
      <S.NotificationPosition>
        <S.FollowButtonContainer>
          <StyledFollowBrandButton
            onClick={() => handleClick(followContent)}
            isFollowedByUser={isFollowedByUser}
          />
        </S.FollowButtonContainer>
        <S.NotificationContainer>
          {!isFollowedByUser && (
            <Notification
              visible
              type="bnnvaraFollowBrandNotification"
              title="Nieuw: volg je favoriete BNNVARA-titels"
              horizontalPosition={isDesktop ? 'left' : 'up'}
              direction={isDesktop ? 'left' : 'up'}
              delay={1000}
              hideAfterSecs={7000}
              OnlyShowOnceEvery24Hrs
              showCloseButton={false}
            >
              {copy}
            </Notification>
          )}
        </S.NotificationContainer>
      </S.NotificationPosition>
    </Fragment>
  );
};

export default FollowBrand;
